import React, { useCallback, useMemo, useState } from "react";
import { BlockStack, InlineStack, TextField } from "@shopify/polaris";
import { LabelWithTooltip } from "./LabelWithTooltip";

export function parseTagsFromString(s) {
  return String(s || "")
    .split(/[,\n]+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

export function joinTagsToString(tags) {
  return tags.join(", ");
}

/**
 * Type a tag and press Enter to add a pill; remove with ×. Value is a comma-separated string (saved in template JSON).
 */
export default function TagInputField({ label, tooltip, value, onChange, disabled }) {
  const tags = useMemo(() => parseTagsFromString(value), [value]);
  const [draft, setDraft] = useState("");

  const commitTags = useCallback(
    (nextTags) => {
      onChange(joinTagsToString(nextTags));
    },
    [onChange],
  );

  const addDraft = useCallback(() => {
    const t = draft.trim();
    if (!t) return;
    const lower = t.toLowerCase();
    if (tags.some((x) => x.toLowerCase() === lower)) {
      setDraft("");
      return;
    }
    commitTags([...tags, t]);
    setDraft("");
  }, [draft, tags, commitTags]);

  const removeAt = useCallback(
    (index) => {
      commitTags(tags.filter((_, i) => i !== index));
    },
    [tags, commitTags],
  );

  const onKeyDown = useCallback(
    (e) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      e.stopPropagation();
      addDraft();
    },
    [addDraft],
  );

  return (
    <BlockStack gap="200">
      <div onKeyDown={onKeyDown}>
        <TextField
          label={
            <LabelWithTooltip
              label={label}
              tooltip={
                tooltip ||
                "Type a tag and press Enter to add it. Tags appear as removable chips below."
              }
            />
          }
          value={draft}
          onChange={setDraft}
          autoComplete="off"
          disabled={disabled}
          placeholder="e.g. Winter"
        />
      </div>
      {tags.length > 0 ? (
        <InlineStack gap="200" wrap blockAlign="center">
          {tags.map((tag, i) => (
            <span
              key={`${tag}-${i}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                background: "var(--p-color-bg-fill-secondary, #e7e8ea)",
                border: "1px solid var(--p-color-border-secondary, #d2d5d8)",
                fontSize: 13,
                lineHeight: 1.2,
                maxWidth: "100%",
              }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 240 }}>
                {tag}
              </span>
              <button
                type="button"
                disabled={disabled}
                aria-label={`Remove ${tag}`}
                onClick={() => removeAt(i)}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: disabled ? "not-allowed" : "pointer",
                  padding: "0 2px",
                  fontSize: 16,
                  lineHeight: 1,
                  color: "var(--p-color-text-secondary, #616161)",
                }}
              >
                ×
              </button>
            </span>
          ))}
        </InlineStack>
      ) : null}
    </BlockStack>
  );
}
